import React, { useEffect } from 'react';
import classnames from 'classnames';
import useId from '@mc/hooks/useId';
import questionStylesheet from '../Question.css';
import {
  getRangeIncrements,
  getRangeDisplayValues,
} from '../../../helpers/utils';
import type { RangePresentation, SurveyHostedQuestion } from '../../../types';
import QuestionLabel from '../QuestionLabel';
import stylesheet from './Range.css';

type RangeIncrementProps = {
  checked: boolean;
  disabled?: boolean;
  displayValue?: string;
  onChange: (value: number) => void;
  presentation?: string;
  rangeIncrement: number;
  question: SurveyHostedQuestion;
  ariaDescribedBy?: string;
};

const radioGroupName = (question: SurveyHostedQuestion) => {
  if (question.id !== null) {
    return String(question.id);
  }
  const fallback = question.properties?.options?.[0]?.value;
  return fallback !== null ? `pending-${fallback}` : 'pending-range';
};

const RangeIncrement = ({
  rangeIncrement,
  displayValue,
  onChange,
  checked,
  presentation,
  disabled = false,
  question,
  ariaDescribedBy,
}: RangeIncrementProps) => {
  const id = useId();
  return (
    <>
      <input
        type="radio"
        id={id}
        value={rangeIncrement}
        className={stylesheet.surveyRadio}
        onChange={() => {
          onChange(rangeIncrement);
        }}
        checked={checked}
        disabled={disabled}
        name={radioGroupName(question)}
        aria-describedby={ariaDescribedBy}
      />
      <label
        className={classnames(stylesheet.surveyRadioLabel, {
          [stylesheet.disabled]: disabled,
          [stylesheet.star]: presentation === 'star',
          [stylesheet.heart]: presentation === 'heart',
          [stylesheet.smiley]: presentation === 'smiley',
        })}
        htmlFor={id}
      >
        {displayValue}
      </label>
    </>
  );
};

type RangeSetProps = {
  disabled?: boolean;
  handleEditResponse: () => void;
  hideLabels?: boolean;
  highRangeLabel?: string;
  highRangeValue?: number;
  lowRangeLabel?: string;
  lowRangeValue?: number;
  onChange: (value: number) => void;
  presentation?: RangePresentation;
  question: SurveyHostedQuestion;
  value: number;
};

const RangeSet = ({
  hideLabels,
  onChange,
  value,
  lowRangeLabel = '',
  highRangeLabel = '',
  lowRangeValue = 0,
  highRangeValue = 10,
  presentation = 'number',
  disabled = false,
  question,
  handleEditResponse,
}: RangeSetProps) => {
  const rangeIncrements = getRangeIncrements(lowRangeValue, highRangeValue);
  const rangeDisplayValues = getRangeDisplayValues(
    presentation,
    lowRangeValue,
    highRangeValue,
  );
  useEffect(() => {
    handleEditResponse();
  }, [handleEditResponse, value]);
  return (
    <>
      <div className={stylesheet.mobileRangeLabelContainer}>
        {lowRangeLabel && (
          <div>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {lowRangeValue} is {lowRangeLabel}
          </div>
        )}
        {highRangeLabel && (
          <div>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {highRangeValue} is {highRangeLabel}
          </div>
        )}
      </div>
      <fieldset className={stylesheet.rangeRadioContainer}>
        {rangeIncrements.map((rangeIncrement, index) => {
          let ariaDescribedBy;
          if (index === 0) {
            ariaDescribedBy = 'lowRangeLabel';
          } else if (index === rangeIncrements.length - 1) {
            ariaDescribedBy = 'highRangeLabel';
          }
          return (
            <RangeIncrement
              rangeIncrement={rangeIncrement}
              presentation={presentation}
              displayValue={rangeDisplayValues[index]}
              checked={rangeIncrement === value}
              key={rangeIncrement}
              onChange={onChange}
              disabled={disabled}
              question={question}
              ariaDescribedBy={ariaDescribedBy}
            />
          );
        })}
      </fieldset>
      {!hideLabels && (
        <div className={stylesheet.rangeLabelContainer}>
          <p className={stylesheet.rangeLabel} id="lowRangeLabel">
            {lowRangeLabel}
          </p>
          <p className={stylesheet.rangeLabel} id="highRangeLabel">
            {highRangeLabel}
          </p>
        </div>
      )}
    </>
  );
};

type RangeProps = {
  handleEditResponse: () => void;
  onChange: (value: number) => void;
  question: SurveyHostedQuestion;
  value: number;
};

const Range = ({
  handleEditResponse,
  question,
  onChange,
  value,
}: RangeProps) => {
  return (
    <div className={questionStylesheet.question}>
      <div className={questionStylesheet.inputWrapper}>
        <QuestionLabel
          className={questionStylesheet.questionLabel}
          question={question}
        />
        <RangeSet
          handleEditResponse={handleEditResponse}
          lowRangeValue={question.properties.lowRangeValue}
          highRangeValue={question.properties.highRangeValue}
          lowRangeLabel={question.properties.lowRangeLabel}
          highRangeLabel={question.properties.highRangeLabel}
          presentation={question.properties.rangePresentation}
          onChange={onChange}
          question={question}
          value={value}
        />
      </div>
    </div>
  );
};

export { Range as default, RangeSet };
